import React, { useRef, useEffect, useState, useCallback } from 'react'
// eslint-disable-next-line import/no-webpack-loader-syntax
import mapboxgl from '!mapbox-gl'
import { useSelector, useDispatch } from 'react-redux'
import { useMedia } from 'react-use'
import Header from '../components/Header'
import ZoomControl from '../components/ZoomControl'
import Modal from '../components/Modal'
import Legend from '../components/Legend'
import LegendTier from '../components/LegendTier'
import ModalTier from '../components/ModalTier'

import {
  setSelectedLayer,
  selectedLayerSelector
} from '../redux/selectedLayerSlice'
import {
  toggleAlternativeMode,
  alternativeModeSelector,
  switchStyle
} from '../redux/mapStyleSlice'
import { setMapPosition } from '../redux/mapSlice'
import {
  ALTERNATIVE_STYLE_URL,
  ALTERNATIVE_STYLE_URL_LIGHT
} from '../utils/styles'

mapboxgl.accessToken =
  'pk.eyJ1IjoicGxhbmV0d2F0Y2hzYXMiLCJhIjoiY2xpMWtvMDFqMDluZjNzbzdtaHVoNXA1OSJ9.gr5ajbSzwQWk8WC-HH1Csg'

const MapComponent = () => {
  const mapContainer = useRef(null)
  const map = useRef(null)
  const isMobile = useMedia('(max-width: 768px)')
  const dispatch = useDispatch()
  const [modalOpen, setModalOpen] = useState(false)
  const [modalData, setModalData] = useState(null)
  const [modalTierData, setModalTierData] = useState(null)
  const [modalodalTierOpen, setModalTierDataOpen] = useState(false)
  const [selectedPointId, setSelectedPointId] = useState(null)
  const [selectedRectangleId, setSelectedRectangleId] = useState(null)

  const [isFirstStyleLoaded, setIsFirstStyleLoaded] = useState(false)
  const [isPollutantsLayerActive, setIsPollutantsLayerActive] = useState(true)

  const alternativeMode = useSelector(alternativeModeSelector)
  const colors = useSelector((state) => state.colors)
  const selectedPollutant = useSelector((state) => state.selectedPollutant)
  const styleUrl = useSelector((state) => state.mapStyle.styleUrl)
  const selectedLayer = useSelector(selectedLayerSelector)
  const layers = useSelector((state) => state.layers)
  const savedZoom = useSelector((state) => state.map.zoom)
  const savedCenter = useSelector((state) => state.map.center)
  const [currentZoom, setCurrentZoom] = useState(savedZoom)

  const handlePointClick = (event) => {
    const clickedPoint = event.point
    const features = map.current.queryRenderedFeatures(clickedPoint, {
      layers: ['pollutants-points']
    })
    if (features.length > 0) {
      const clickedFeature = features[0]
      const clickedId = clickedFeature.id

      if (map.current.getLayer('pollutants-points')) {
        map.current.setPaintProperty(
          'pollutants-points',
          'circle-color',
          ['match', ['id'], clickedId, 'blue', colors[selectedLayer]]
        )

        map.current.setPaintProperty(
          'pollutants-points',
          'circle-blur',
          ['match', ['id'], clickedId, 0, 1]
        )
        map.current.setPaintProperty(
          'pollutants-points',
          'circle-stroke-width',
          ['match', ['id'], clickedId, 4, 2]
        )
        map.current.setPaintProperty(
          'pollutants-points',
          'circle-stroke-color',
          ['match', ['id'], clickedId, '#63EE55', 'transparent']
        )
      }

      setSelectedPointId(clickedId)
      setModalData(features)
      setModalOpen(true)
    }
  }

  const handleMapClick = (event) => {
    if (styleUrl === ALTERNATIVE_STYLE_URL || ALTERNATIVE_STYLE_URL_LIGHT) {
      const features = map.current.queryRenderedFeatures(event.point, {
        layers: ['coverage-hi-res']
      })

      if (features.length) {
        const geohash = features[0].properties.geohash

        setModalTierData(features[0].properties)
        setModalTierDataOpen(true)
        setSelectedRectangleId(geohash)

        if (map.current.getLayer('coverage-hi-res')) {
          map.current.setPaintProperty(
            'coverage-hi-res',
            'fill-outline-color',
            [
              'match',
              ['get', 'geohash'],
              geohash,
              'blue',
              'transparent'
            ]
          )
        }
      } else {
        setSelectedRectangleId(null)
      }
    }
  }

  const handleLayerChange = (layer) => {
    dispatch(setSelectedLayer(layer))

    if (map.current && !map.current.getLayer('pollutants-points')) {
      map.current.addSource('pollutants-points', {
        type: 'vector',
        url: 'mapbox://planetwatchsas.outdoor'
      })

      map.current.addLayer({
        id: 'pollutants-points',
        type: 'circle',
        source: 'pollutants-points',
        'source-layer': 'pollutants',
        paint: {
          'circle-color': ['case',
            ['==', ['get', 'id'], selectedPointId],
            '#000',
            colors[selectedLayer]
          ],
          'circle-opacity': 0.8,
          'circle-radius':
           ['interpolate', ['linear'], ['zoom'],
             2, 5,
             5, 11,
             10, 21,
             15, 32,
             20, 39
           ],
          'circle-blur': 1,
          'circle-stroke-width': 2,
          'circle-stroke-color': 'transparent'
        },
        layout: {
          visibility: isPollutantsLayerActive ? 'visible' : 'none'
        }
      })
    } else if (map.current.getLayer('pollutants-points')) {
      map.current.setPaintProperty(
        'pollutants-points',
        'circle-color',
        colors[layer]
      )
    }
    if (alternativeMode) {
      dispatch(toggleAlternativeMode())
      dispatch(switchStyle())
    }
    dispatch(
      setMapPosition({
        center: map.current.getCenter(),
        zoom: map.current.getZoom()
      })
    )
    setIsPollutantsLayerActive(true)
  }

  const handleStyleChange = useCallback(() => {
    if (!isPollutantsLayerActive) {
      dispatch(setMapPosition({ center: savedCenter, zoom: savedZoom }))
    }
    dispatch(
      setMapPosition({
        center: map.current.getCenter(),
        zoom: map.current.getZoom()
      })
    )

    dispatch(toggleAlternativeMode())

    dispatch(switchStyle())
    setIsPollutantsLayerActive(!isPollutantsLayerActive)
  }, [isPollutantsLayerActive, savedCenter, savedZoom, dispatch])

  const handleZoomIn = () => {
    const newZoom = currentZoom + 1
    setCurrentZoom(newZoom)
    map.current.setZoom(newZoom)
  }

  const handleZoomOut = () => {
    const newZoom = currentZoom - 1
    setCurrentZoom(newZoom)
    map.current.setZoom(newZoom)
  }

  const closeModal = () => {
    setModalOpen(false)

    if (map.current.getLayer('pollutants-points')) {
      map.current.setPaintProperty(
        'pollutants-points',
        'circle-color',
        colors[selectedLayer]
      )

      map.current.setPaintProperty(
        'pollutants-points',
        'circle-blur',
        1
      )
    }

    if (map.current.getLayer('coverage-hi-res')) {
      setModalTierDataOpen(false)
      map.current.setPaintProperty('coverage-hi-res', 'fill-color', 'transparent')
      map.current.setPaintProperty('coverage-hi-res', 'fill-outline-color', 'transparent')
    }
  }

  useEffect(() => {
    const initialZoom = isMobile ? 1 : 3
    const initialCenter = [-36.918, 37.743]
    dispatch(setMapPosition({ center: initialCenter, zoom: initialZoom }))
  }, [isMobile, dispatch])

  useEffect(() => {
    if (map.current) return
    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: styleUrl,
      center: savedCenter,
      zoom: savedZoom,
      minZoom: 2.5,
      maxZoom: 18,
      attributionControl: false
    })

    map.current.on('load', () => {
      setIsFirstStyleLoaded(true)

      map.current.addSource('pollutants-points', {
        type: 'vector',
        url: 'mapbox://planetwatchsas.outdoor'
      })

      map.current.addLayer({
        id: 'pollutants-points',
        type: 'circle',
        source: 'pollutants-points',
        'source-layer': 'pollutants',
        paint: {
          'circle-color': colors[selectedLayer],
          'circle-opacity': 0.8,
          'circle-radius':
           ['interpolate', ['linear'], ['zoom'],
             2, 6,
             5, 11,
             10, 21,
             15, 32,
             20, 39
           ],
          'circle-blur': 1
        },
        layout: {
          visibility: isPollutantsLayerActive ? 'visible' : 'none'
        }
      })

      map.current.on('click', 'pollutants-points', handlePointClick)

      if (styleUrl === ALTERNATIVE_STYLE_URL) {
        map.current.on('click', handleMapClick)
      }
      if (styleUrl === ALTERNATIVE_STYLE_URL_LIGHT) {
        map.current.on('click', handleMapClick)
      }
    })

    return () => {
      if (map.current) {
        map.current.off('click', 'pollutants-points', handlePointClick)
        map.current.on('click', handleMapClick)
        map.current.remove()
        map.current = null
      }
    }
  }, [
    savedCenter,
    savedZoom,
    styleUrl,
    colors,
    selectedPollutant,
    selectedLayer,
    isPollutantsLayerActive,
    layers
  ])

  return (
    <div>
      <Header mapRef={map} />

      {isPollutantsLayerActive
        ? (
          <Legend selectedLayer={selectedLayer} />
          )
        : (
          <LegendTier />
          )}

      <div
        className='mapContainer'
        style={{ height: '100vh' }}
        ref={mapContainer}
      />
      <ZoomControl
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
        handleMyLocationClick={() => {}}
        handleToggleLayer={handleLayerChange}
        handleStyleChange={handleStyleChange}
        isPollutantsLayerActive={isPollutantsLayerActive}
        map={map}
      />
      {isPollutantsLayerActive
        ? (
          <Modal
            isOpen={modalOpen}
            closeModal={closeModal}
            modalData={modalData}
          />
          )
        : (
          <ModalTier
            isOpen={modalodalTierOpen}
            closeModal={closeModal}
            modalData={modalTierData}
          />
          )}
    </div>
  )
}

export default React.memo(MapComponent)
